import { jsx as _jsx } from "react/jsx-runtime";
import { DialogModal } from '#pplx/frontend/aether/components/Modal/internal/DialogModal.js';
import { ModalBody } from '#pplx/frontend/aether/components/Modal/internal/ModalBody.js';
import { ModalErrorBoundary } from '#pplx/frontend/aether/components/Modal/internal/ModalErrorBoundary.js';
import { ModalFooter } from '#pplx/frontend/aether/components/Modal/internal/ModalFooter.js';
import { ModalHeader, ModalTitle, } from '#pplx/frontend/aether/components/Modal/internal/ModalHeader.js';
import { SideSheetModal } from '#pplx/frontend/aether/components/Modal/internal/SideSheetModal.js';
import { useModalExitAnimation } from '#pplx/frontend/aether/components/Modal/internal/useModalExitAnimation.js';
// ---------------------------------------------------------------------------
// Modal component
// ---------------------------------------------------------------------------
/**
 * Simple, accessible modal dialog built on Radix UI Dialog.
 *
 * Provides a centered panel with backdrop overlay, focus trapping, and
 * Escape-to-close out of the box. Designed to work seamlessly with the
 * aether modal management system (`openModal`) as well as standalone
 * controlled usage.
 *
 * On mobile devices (when `isMobileStyle` is true in AetherProvider),
 * automatically renders as a bottom sheet instead of a centered dialog.
 *
 * Uses compound sub-components for structured layout. Each sub-component
 * renders its own DOM — no implicit child inspection or reordering:
 *
 * - `Modal.Header` — title and optional description
 * - `Modal.Body` — scrollable content area with gradient overlays
 * - `Modal.Footer` — fixed action bar pinned to the bottom
 *
 * @example
 * ```tsx
 * // Simple — bare children (no scrollable body or footer)
 * <Modal onClose={onClose}>
 *   <Modal.Header title="Confirm" />
 *   <p>Are you sure?</p>
 * </Modal>
 *
 * // With header, body, and footer
 * <Modal onClose={onClose}>
 *   <Modal.Header title="Edit bookmark" />
 *   <Modal.Body>
 *     <FormField label="Name" />
 *     <FormField label="URL" />
 *   </Modal.Body>
 *   <Modal.Footer>
 *     <Button variant="text">Remove</Button>
 *     <Button>Done</Button>
 *   </Modal.Footer>
 * </Modal>
 * ```
 */
function ModalRoot(props) {
    return (_jsx(ModalErrorBoundary, { isOpen: props.isOpen, onClose: props.onClose, variant: props.variant ?? 'dialog', children: _jsx(ModalRootContent, { ...props }) }));
}
function ModalRootContent(props) {
    const { isOpen = true } = props;
    const exitAnimation = useModalExitAnimation(isOpen);
    // Without a lifecycle provider, the standalone Modal's `isOpen` prop remains authoritative.
    if (props.variant === 'side-sheet') {
        return (_jsx(SideSheetModal, { ...props, isOpen: exitAnimation.isOpen, onExitComplete: exitAnimation.onExitComplete }));
    }
    return (_jsx(DialogModal, { ...props, isOpen: exitAnimation.isOpen, onExitComplete: exitAnimation.onExitComplete }));
}
// ---------------------------------------------------------------------------
// Public API — Modal with compound components
// ---------------------------------------------------------------------------
export const Modal = Object.assign(ModalRoot, {
    Header: ModalHeader,
    Title: ModalTitle,
    Body: ModalBody,
    Footer: ModalFooter,
});
//# sourceMappingURL=index.js.map